import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

import populateSignals from "../../utils/heatmap/populateSignals";
import populateFilters from "../../utils/heatmap/populateFilters";
// import populateDrillDownBy from "../../utils/heatmap/populateDrillDownBy";
import adjustForCustomLayers from "../../utils/heatmap/adjustForCustomLayers";
import populateMapToReadableForm from "../../utils/heatmap/populateMapToReadableForm";
import truncateData from "../../utils/heatmap/populateData/truncateData";
import retrieveCustomLayers from "../../utils/heatmap/retrieveCustomLayers";
import mergePatternsAndIndicators from "../../utils/heatmap/populateData/mergePatternsAndIndicators";
import processData from "../../utils/heatmap/populateData/processData";

const heatMapSlice = createSlice({
  name: "heatMap",
  initialState: {
    rawSignalData: null,
    populatedSignals: null,
    drillDownBy: [
      "Market Cap",
      "Name",
      "Zone",
      "chart_name",
      "interval",
      "model_name",
    ],
    confidenceRange: [0.5, 1],
    filters: {
      chart_name: [],
      interval: [],
      model_name: [],
      "Market Cap": [],
      Name: [],
      Zone: [],
    },
    depthConstraint: 1,
    inFront: 1,
    zoomInTileData: null,
    triggerRerender: {
      heatMap1: { count: 0 },
      heatMap2: { count: 0 },
    },
    selectedCharts: [],
    customLayers: retrieveCustomLayers(),
    mapToReadableForm: {
      chart_name: "Security",
      interval: "Interval",
      model_name: "Signal Type",
    },
    warningMessage: null,
    barRange: [0, 30],
    showMissingSignals: false,
    warning: {
      messages: [],
      renderFirstTime: true,
    },
    showHeatMapChart: false,
    apiLoadingProgress: 100,
    apiLastSyncTime: null,
    buyOrSell: null,
    activeOrFormed: null,
    patternsOrIndicators: "patterns",

    transitioning: false,
  },
  reducers: {
    setRawSignalData: (state, action) => {
      state.rawSignalData = action.payload;
    },
    updateHeatMap: (state, action) => {
      const {
        filters,
        drillDownBy,
        depthConstraint,
        confidenceRange,
        barRange,
        showMissingSignals,
        buyOrSell,
        activeOrFormed,
        patternsOrIndicators,
      } = action.payload;

      state.filters = filters;
      state.drillDownBy = drillDownBy;
      state.depthConstraint = depthConstraint;
      state.confidenceRange = confidenceRange;
      state.barRange = barRange;
      state.selectedCharts = [];
      state.showMissingSignals = showMissingSignals;
      state.buyOrSell = buyOrSell;
      state.activeOrFormed = activeOrFormed;
      state.patternsOrIndicators = patternsOrIndicators;
    },
    setPopulatedSignalsData: (state, action) => {
      const {
        newPopulatedSignalsAdjusted = null,
        newFilters = {
          chart_name: [],
          interval: [],
          model_name: [],
          Sector: [],
          "Asset Group": [],
          "Contract Type": [],
        },
        newDrillDownBy = ["chart_name", "interval", "model_name"],
        newMapToReadableForm = {
          chart_name: "Security",
          interval: "Interval",
          model_name: "Signal Type",
        },
        isConfigSelected,
      } = action.payload;
      state.populatedSignals = newPopulatedSignalsAdjusted;
      state.mapToReadableForm = newMapToReadableForm;

      if (!isConfigSelected) {
        state.filters = newFilters;
        state.drillDownBy = newDrillDownBy;
      }
    },
    setInFront: (state, action) => {
      state.inFront = action.payload;
    },
    setZoomInTileData: (state, action) => {
      state.zoomInTileData = action.payload;
    },
    triggerRerender: (state, action) => {
      const index = action.payload;
      state.triggerRerender[`heatMap${index}`] = {
        count: state.triggerRerender[`heatMap${index}`].count++,
      };
    },
    addChart: (state, action) => {
      const duplicate = state.selectedCharts.filter((selectedChart) =>
        _.isEqual(selectedChart, action.payload)
      );
      if (!duplicate.length) {
        state.selectedCharts.push(action.payload);
      }
    },
    removeChart: (state, action) => {
      const indexToRemove = state.selectedCharts.findIndex((chartData) =>
        _.isEqual(chartData, action.payload)
      );
      state.selectedCharts.splice(indexToRemove, 1);
      if (state.selectedCharts.length) {
        state.triggerRerender[`heatMap${state.inFront}`] = {
          count: state.triggerRerender[`heatMap${state.inFront}`].count++,
        };
      }
    },
    removeAllCharts: (state) => {
      state.selectedCharts = [];
    },
    setCustomLayers: (state, action) => {
      state.customLayers = action.payload;
    },
    setWarningMessage: (state, action) => {
      state.warningMessage = action.payload;
    },
    upOneLayer: (state) => {
      state.depthConstraint = state.depthConstraint - 1;
    },
    updateWarning: (state, action) => {
      state.warning = { ...state.warning, ...action.payload };
    },
    setShowHeatMapChart: (state, action) => {
      state.showHeatMapChart = action.payload;
    },
    setApiLoadingProgress: (state, action) => {
      if (action.payload.action === "reset") {
        state.apiLoadingProgress = 0;
      } else if (action.payload.action === "increment") {
        state.apiLoadingProgress = Math.min(
          state.apiLoadingProgress + action.payload.value,
          100
        );
        if (Math.round(state.apiLoadingProgress) === 100)
          state.apiLoadingProgress = 100;
      }
    },
    setApiLastSyncTime: (state, action) => {
      state.apiLastSyncTime = action.payload;
    },
    setTransitioning: (state, action) => {
      state.transitioning = action.payload;
    },
  },
});

export const {
  setPopulatedSignalsData,
  updateHeatMap,
  setInFront,
  setZoomInTileData,
  triggerRerender,
  addChart,
  removeChart,
  removeAllCharts,
  setCustomLayers,
  setWarningMessage,
  upOneLayer,
  updateWarning,
  setShowHeatMapChart,
  setRawSignalData,
  setApiLoadingProgress,
  setApiLastSyncTime,
  setTransitioning,
} = heatMapSlice.actions;

export default heatMapSlice.reducer;

export const populateData =
  (signalData, models, customLayers) => async (dispatch, getState) => {
    if (!signalData) return;

    const { chartCollectionIds, patternsData, indicatorsData } = signalData;
    if (
      !patternsData ||
      !patternsData.length ||
      !chartCollectionIds ||
      !models.length
    )
      return;

    const data = mergePatternsAndIndicators(patternsData, indicatorsData);

    const processedData = processData(data);

    const newPopulatedSignals = await populateSignals(
      chartCollectionIds,
      processedData,
      models
    );

    const newPopulatedSignalsTruncated = truncateData(newPopulatedSignals);

    const newPopulatedSignalsAdjusted = adjustForCustomLayers(
      newPopulatedSignalsTruncated,
      customLayers
    );

    const customLayersCategories = customLayers
      ? Object.keys(customLayers[0]).filter((el) => el !== "Security")
      : [];

    const newFilters = populateFilters(
      newPopulatedSignalsAdjusted,
      customLayersCategories
    );

    const newDrillDownBy = getState().heatMap.drillDownBy;

    const newMapToReadableForm = populateMapToReadableForm(
      customLayersCategories
    );

    const isConfigSelected = getState().heatMapConfigs.selectedConfig !== "";

    dispatch(
      setPopulatedSignalsData({
        newPopulatedSignalsAdjusted,
        newFilters,
        newDrillDownBy,
        newMapToReadableForm,
        isConfigSelected,
      })
    );
  };
